import { useCallback, useEffect, useRef, useState } from 'react';
import DragAndDrop from './drag-drop';
import Modal from './modal';
import { fetch } from '../utils/api-wrapper';
import {
    buildAttachmentsListUrl,
    partitionAttachmentsByOwnerSubId,
    shouldGroupAttachmentsByOwnerSubId,
    sortAttachmentGroupsByLineNumber,
} from '../utils/attachments';

const ALLOWED_MIME_TYPES = ['application/pdf', 'image/png', 'image/jpeg'];
const IMAGE_MIME_TYPES = ['image/png', 'image/jpeg'];
const ACCEPT_ATTRIBUTE = ALLOWED_MIME_TYPES.join(',');
// Truncate unknown owner_sub_id keys in the XPEN-3118 fallback group label.
const GROUP_FALLBACK_KEY_LENGTH = 8;

const formatDate = (value) => {
    if (!value) return '';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString();
};

// GCS signed URLs are always https:// — this is an intentional allow-list
// enforced before the value ever reaches an `<img src>` / `<object data>`
// sink, not an incidental limitation of a test/dev environment.
const isHttpsUrl = (url) => typeof url === 'string' && url.startsWith('https://');

const MAX_UPLOAD_SIZE_BYTES = 25 * 1024 * 1024;

const buildOwnerKey = (ownerType, ownerId, ownerSubId) => `${ownerType}|${ownerId}|${ownerSubId ?? ''}`;

// Mirrors the backend's rule exactly (AttachmentsService.get_signed_url,
// XPEN-3083 + its PR review follow-up): an explicit allowlist, not a
// deny-list on "infected" alone — a missing/corrupt scan_status must not
// enable a click that can only fail with attachment_not_ready. No
// functioning virus scanner exists yet (Epic XPEN-9) to ever produce
// "clean", so gating on that value alone left every attachment
// permanently un-openable.
const isOpenable = (attachment) => ['pending', 'clean'].includes(attachment.scan_status);

const buildUploadFormData = (file, ownerType, ownerId, ownerSubId) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('owner_type', ownerType);
    formData.append('owner_id', ownerId);
    if (ownerSubId) formData.append('owner_sub_id', ownerSubId);
    return formData;
};

/**
 * Lists, uploads and deletes the supporting files attached to an invoice,
 * credit-card receipt or expense line (XPEN-3003).
 *
 * @param {object} props
 * @param {object} props.data - XPENS store data, used for translations.
 * @param {"invoice"|"cc_receipt"|"expense"} props.ownerType
 * @param {string} props.ownerId
 * @param {string} [props.ownerSubId] - Expense line or transaction checksum.
 * @param {(count: number) => void} [props.onAttachmentsChanged] - Called with
 *   the current attachment count after every successful list load (initial
 *   load, and the automatic reload after an upload or delete settles) — lets
 *   a host like PDFVisualizer keep a count it displays elsewhere (e.g. a
 *   badge) in sync without needing its own refresh logic (XPEN-3004).
 * @param {Object.<string, {lineNumber: number, amount: (string|null), details: string[]}>} [props.attachmentGroupLabels]
 *   Optional map of owner_sub_id → label parts from ListItem (XPEN-3118).
 *   The panel adds the per-group attachment count at render time.
 */
const AttachmentsPanel = ({ data, ownerType, ownerId, ownerSubId, onAttachmentsChanged, attachmentGroupLabels }) => {
    const ownerKey = buildOwnerKey(ownerType, ownerId, ownerSubId);
    // Tracks the owner this component last rendered for. Compared against
    // `ownerKey` during render (see below) rather than in a useEffect — an
    // effect only runs after the browser has painted, so resetting there
    // would let one frame commit with the *new* owner's props but the
    // *previous* owner's attachments/preview/delete-modal still in state.
    const [renderedOwnerKey, setRenderedOwnerKey] = useState(ownerKey);

    const [attachments, setAttachments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState(null);
    // Distinct from `error` (which is shared by upload/open/delete failures
    // too) — this specifically means "the list itself failed to load", the
    // only case where showing the empty-state upload zone would be
    // misleading. Gating on the shared `error` instead would also hide the
    // zone after an unrelated upload failure on an already-empty record.
    const [hasLoadFailed, setHasLoadFailed] = useState(false);
    const [validationError, setValidationError] = useState(null);
    const [openedAttachmentId, setOpenedAttachmentId] = useState(null);
    const [openedUrl, setOpenedUrl] = useState(null);
    const [deleteCandidate, setDeleteCandidate] = useState(null);
    // The attachment whose date/source/uploader details are shown in the
    // info modal (XPEN-3088) — separate from deleteCandidate, since both
    // can't be open at once but are otherwise unrelated actions.
    const [infoAttachment, setInfoAttachment] = useState(null);

    // Discards a list response superseded by a newer one (e.g. the owner
    // props changed while a fetch was in flight).
    const listRequestIdRef = useRef(0);
    // Same idea, scoped to signed-url requests — clicking a second row
    // before the first one's link arrives must not paint the first file's
    // content under the second file's name.
    const openRequestIdRef = useRef(0);
    // The very first load for a given owner blocks the whole panel on
    // "Loading…"; a later background refresh (post-upload/delete, same
    // owner) must not blank an already-shown list, or a just-set error
    // message flickers away and back. Reset to false whenever the owner
    // props change — that IS a real "new record" load, not a refresh.
    const hasLoadedOnceRef = useRef(false);
    // Shared across overlapping upload batches (a second drop while the
    // first is still in flight) — a per-call counter would let whichever
    // batch settles first clear `uploading` while the other is still going.
    const uploadsInFlightRef = useRef(0);
    // Bumped only by the render-phase owner reset below — every async
    // callback below captures the current value when it starts and compares
    // against it before touching state, so a result belonging to any owner
    // the user has since navigated away from is always discarded. This must
    // be a monotonic counter, not an identity key derived from the owner
    // props: navigating A → B → A produces the same key for A both times, so
    // a callback from the *first* visit to A would incorrectly pass an
    // identity check once the user returns to A. A counter that only ever
    // increases never repeats, so it has no such blind spot.
    const ownerGenerationRef = useRef(0);
    // Mirrors `openedAttachmentId` for reads inside async callbacks —
    // `confirmDelete` must compare against the *current* opened attachment,
    // not the one captured in its own closure at click-time, or a
    // same-generation auto-open that happens while the delete is still in
    // flight (e.g. an unrelated upload settling in between) would be missed.
    const openedAttachmentIdRef = useRef(null);
    const isMountedRef = useRef(true);
    useEffect(() => () => {
        isMountedRef.current = false;
    }, []);

    const setOpenedAttachment = (attachmentId, url) => {
        openedAttachmentIdRef.current = attachmentId;
        setOpenedAttachmentId(attachmentId);
        setOpenedUrl(url);
    };

    // React's documented "adjusting state when a prop changes" pattern:
    // detected and reset DURING render, not in a useEffect. An effect only
    // runs after React has already committed and the browser has painted —
    // for a single frame, that would show the *new* owner's props next to
    // the *previous* owner's attachments/preview/delete-modal. Resetting
    // here means React re-renders with cleared state before anything is
    // ever painted, so the previous record's data can never flash on screen.
    if (ownerKey !== renderedOwnerKey) {
        setRenderedOwnerKey(ownerKey);
        ownerGenerationRef.current += 1;
        hasLoadedOnceRef.current = false;
        uploadsInFlightRef.current = 0;
        setAttachments([]);
        setLoading(true);
        setError(null);
        setHasLoadFailed(false);
        setValidationError(null);
        setUploading(false);
        setOpenedAttachment(null, null);
        setDeleteCandidate(null);
        setInfoAttachment(null);
    }

    const openAttachment = useCallback(
        (attachment, { clearError = true } = {}) => {
            if (!isOpenable(attachment)) return;
            const generation = ownerGenerationRef.current;
            const requestId = ++openRequestIdRef.current;
            // The auto-open-on-single-file path (below) passes
            // clearError: false — it must never wipe an upload/delete
            // failure the very same refresh cycle just surfaced.
            if (clearError) setError(null);
            setOpenedAttachment(attachment.attachment_id, null);
            fetch(
                `/attachments/${encodeURIComponent(attachment.attachment_id)}/signed-url`,
                'GET',
                {},
                (response) => {
                    if (!isMountedRef.current) return;
                    if (generation !== ownerGenerationRef.current || requestId !== openRequestIdRef.current) return;
                    const url = response?.data?.url;
                    if (isHttpsUrl(url)) {
                        setOpenedUrl(url);
                    } else {
                        setError(data.t.read('error_attachment_open_failed'));
                    }
                },
                () => {
                    if (!isMountedRef.current) return;
                    if (generation !== ownerGenerationRef.current || requestId !== openRequestIdRef.current) return;
                    setError(data.t.read('error_attachment_open_failed'));
                }
            );
        },
        [data]
    );

    const loadAttachments = useCallback(() => {
        const generation = ownerGenerationRef.current;
        const requestId = ++listRequestIdRef.current;
        if (!hasLoadedOnceRef.current) setLoading(true);
        // Deliberately does not clear `error` here: this also runs as a
        // background refresh after upload/delete, and must not erase the
        // very failure message that action just set.
        fetch(
            buildAttachmentsListUrl(ownerType, ownerId, ownerSubId),
            'GET',
            {},
            (response) => {
                if (!isMountedRef.current) return;
                if (generation !== ownerGenerationRef.current || requestId !== listRequestIdRef.current) return;
                const list = response?.data?.attachments;
                const validList = Array.isArray(list) ? list : [];
                setAttachments(validList);
                setLoading(false);
                setHasLoadFailed(false);
                hasLoadedOnceRef.current = true;
                onAttachmentsChanged?.(validList.length);
                // Design §5.2.2 — a lone openable file previews immediately;
                // several files only open on click.
                if (validList.length === 1 && isOpenable(validList[0])) {
                    openAttachment(validList[0], { clearError: false });
                }
            },
            () => {
                if (!isMountedRef.current) return;
                if (generation !== ownerGenerationRef.current || requestId !== listRequestIdRef.current) return;
                setError(data.t.read('error_attachment_load_failed'));
                setLoading(false);
                setHasLoadFailed(true);
                hasLoadedOnceRef.current = true;
            }
        );
    }, [data, ownerType, ownerId, ownerSubId, openAttachment, onAttachmentsChanged]);

    useEffect(() => {
        // State for a genuinely new owner is already reset above, during
        // render — this effect only ever needs to kick off the fetch.
        loadAttachments();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ownerType, ownerId, ownerSubId]);

    const handleFiles = (fileList) => {
        setError(null);
        const generation = ownerGenerationRef.current;
        const files = Array.from(fileList || []);
        const isAllowedType = (file) => ALLOWED_MIME_TYPES.includes(file.type);
        const validFiles = files.filter((file) => isAllowedType(file) && file.size <= MAX_UPLOAD_SIZE_BYTES);
        // Server enforces the same 25 MB cap — this check exists purely so
        // the user gets an instant, specific message instead of a failed
        // upload after a round trip, matching the "Add New Invoice" pattern.
        const hasTooLarge = files.some((file) => isAllowedType(file) && file.size > MAX_UPLOAD_SIZE_BYTES);
        const hasInvalidType = files.some((file) => !isAllowedType(file));

        // Kept separate from the network `error` state — a reload triggered
        // by one valid file in the same selection must not erase this notice.
        setValidationError(
            hasTooLarge
                ? data.t.read('error_attachment_file_too_large')
                : hasInvalidType
                    ? data.t.read('invalid_upload_file_type')
                    : null
        );
        if (validFiles.length === 0) return;

        uploadsInFlightRef.current += validFiles.length;
        setUploading(true);
        const onOneFileSettled = () => {
            // The owner changed since this batch started — the reset effect
            // already zeroed the shared counter for the new owner, so this
            // stale batch must not touch it (or `uploading`) at all.
            if (generation !== ownerGenerationRef.current) return;
            uploadsInFlightRef.current -= 1;
            if (uploadsInFlightRef.current === 0 && isMountedRef.current) {
                setUploading(false);
                loadAttachments();
            }
        };

        validFiles.forEach((file) => {
            fetch(
                '/attachments',
                'POST',
                buildUploadFormData(file, ownerType, ownerId, ownerSubId),
                onOneFileSettled,
                () => {
                    if (isMountedRef.current && generation === ownerGenerationRef.current) {
                        setError(data.t.read('error_attachment_upload_failed'));
                    }
                    onOneFileSettled();
                },
                true
            );
        });
    };

    const confirmDelete = () => {
        setError(null);
        const generation = ownerGenerationRef.current;
        const attachmentId = deleteCandidate.attachment_id;
        fetch(
            `/attachments/${encodeURIComponent(attachmentId)}`,
            'DELETE',
            {},
            () => {
                if (!isMountedRef.current || generation !== ownerGenerationRef.current) return;
                // Reads the ref, not the `openedAttachmentId` captured when
                // confirmDelete was invoked — an unrelated auto-open can
                // resolve while this delete is still in flight, and the
                // check must reflect whichever attachment is open *now*.
                if (openedAttachmentIdRef.current === attachmentId) {
                    setOpenedAttachment(null, null);
                    // Invalidates a signed-url request still in flight for
                    // the attachment just deleted, so its response can't
                    // resurrect a preview of a file that no longer exists.
                    openRequestIdRef.current += 1;
                }
                setDeleteCandidate(null);
                loadAttachments();
            },
            () => {
                if (!isMountedRef.current || generation !== ownerGenerationRef.current) return;
                // Close the modal on failure too, so the panel-level error
                // banner (rendered behind the modal overlay otherwise) is
                // visible. The user can just retry the delete action.
                setDeleteCandidate(null);
                setError(data.t.read('error_attachment_delete_failed'));
            }
        );
        // No return value — Modal's own processing flag stays engaged
        // (button disabled) for as long as the modal remains mounted, which
        // is exactly until one of the callbacks above closes it.
    };

    const renderStatusBadge = (attachment) => {
        // No "still being checked" message for `pending`: nothing is
        // actually checking it (no virus scanner exists yet, Epic XPEN-9),
        // so that message just described a check that never happens. On
        // hold until a real scanner lands and this can say something true.
        if (attachment.scan_status === 'infected') {
            return (
                <span className="text-danger ml-2" data-testid={`attachment-status-${attachment.attachment_id}`}>
                    {data.t.read('attachments_status_infected')}
                </span>
            );
        }
        return null;
    };

    const renderDeleteButton = (attachment) => (
        <button
            type="button"
            className="btn btn-link text-danger p-0"
            aria-label={`${data.t.read('delete')} ${attachment.file_name}`}
            onClick={() => setDeleteCandidate(attachment)}
        >
            {data.t.read('delete')}
        </button>
    );

    // Replaces the old always-visible date/source/uploader column (XPEN-3088)
    // — a plain sibling button, not an overlay, opening a modal with the
    // same details on demand. Keeps every row down to name/status/delete,
    // which is also what let the row become a fixed-width grid instead of
    // an unconstrained flex row whose columns drifted with file name length.
    const renderInfoButton = (attachment) => (
        <button
            type="button"
            className="btn attachments-info-button"
            aria-label={`${data.t.read('attachments_info_label')} ${attachment.file_name}`}
            onClick={() => setInfoAttachment(attachment)}
        >
            i
        </button>
    );

    const renderAddAnother = () => (
        <label className="btn btn-primary mt-4" data-testid="attachments-add-another">
            {data.t.read('attachments_add_another')}
            <input
                type="file"
                accept={ACCEPT_ATTRIBUTE}
                style={{ display: 'none' }}
                multiple
                disabled={uploading}
                onChange={(evt) => { handleFiles(evt.target.files); evt.target.value = ''; }}
                data-testid="attachments-add-another-input"
            />
        </label>
    );

    const renderPreview = () => {
        if (!openedUrl) return null;
        const openedAttachment = attachments.find((item) => item.attachment_id === openedAttachmentId);
        // A background refresh no longer containing the opened attachment
        // (e.g. it was just deleted) must render nothing here, not the
        // "unsupported file" fallback below — that message would wrongly
        // blame the browser for a file that simply no longer exists.
        if (!openedAttachment) return null;
        const mimeType = openedAttachment.mime_type;
        if (IMAGE_MIME_TYPES.includes(mimeType)) {
            return (
                <img
                    src={openedUrl}
                    alt={openedAttachment.file_name}
                    className="attachments-preview"
                    data-testid="attachments-preview"
                />
            );
        }
        // Re-validated against the allow-list rather than defaulting every
        // non-image mime type to a PDF object — an unexpected type (e.g.
        // from a future ingestor source) must fall back, not be embedded.
        if (mimeType === 'application/pdf') {
            return (
                <object
                    width="100%"
                    height="100%"
                    type="application/pdf"
                    data={openedUrl}
                    aria-label={data.t.read('attachments_preview_pdf_label')}
                    className="attachments-preview"
                    data-testid="attachments-preview"
                >
                    <div>{data.t.read('attachments_preview_unsupported')}</div>
                </object>
            );
        }
        return <div className="attachments-preview" data-testid="attachments-preview">{data.t.read('attachments_preview_unsupported')}</div>;
    };

    const renderUploadZone = () => (
        <DragAndDrop handleDrop={handleFiles}>
            <div className="first-files-container" data-testid="attachments-empty">
                <div className="font-weight-bold mt-3">{data.t.read('attachments_upload_title')}</div>
                <div className="font-small semi-faded mb-3 browse-files">{data.t.read('browse_files')}</div>
                <label className="btn btn-primary pl-5 pr-5">
                    {data.t.read('browse')}
                    <input
                        type="file"
                        accept={ACCEPT_ATTRIBUTE}
                        style={{ display: 'none' }}
                        multiple
                        disabled={uploading}
                        onChange={(evt) => { handleFiles(evt.target.files); evt.target.value = ''; }}
                        data-testid="attachments-file-input"
                    />
                </label>
            </div>
        </DragAndDrop>
    );

    const renderSingleFile = () => {
        const attachment = attachments[0];
        const openable = isOpenable(attachment);
        return (
            <div className="attachments-panel-item" data-testid="attachments-single">
                {/* Fixed-width columns (name/status/info/delete), not an
                    unconstrained flex row — a flexible name column shifted
                    every column after it out of alignment with the list
                    view's rows depending on file name length (XPEN-3088). */}
                <div className="attachments-row">
                    {/* A button, not static text — lets the user retry after
                        a failed auto-open. Disabled only while `!openable`
                        (currently: `infected`), so a `pending` file is
                        clickable immediately, not gated on a later reload. */}
                    <button
                        type="button"
                        className="btn btn-link text-left p-0 font-weight-bold text-truncate"
                        onClick={() => openAttachment(attachment)}
                        disabled={!openable}
                        data-testid="attachment-name"
                    >
                        {attachment.file_name}
                    </button>
                    <div className="attachments-row-status">{renderStatusBadge(attachment)}</div>
                    {renderInfoButton(attachment)}
                    {renderDeleteButton(attachment)}
                </div>
                {renderPreview()}
                {renderAddAnother()}
            </div>
        );
    };

    const renderAttachmentRow = (attachment) => {
        const openable = isOpenable(attachment);
        return (
            <div
                key={attachment.attachment_id}
                className="attachments-row py-2 border-bottom"
                data-testid={`attachment-row-${attachment.attachment_id}`}
            >
                <button
                    type="button"
                    className="btn btn-link text-left p-0 text-truncate"
                    onClick={() => openAttachment(attachment)}
                    disabled={!openable}
                >
                    {attachment.file_name}
                </button>
                <div className="attachments-row-status">{renderStatusBadge(attachment)}</div>
                {renderInfoButton(attachment)}
                {renderDeleteButton(attachment)}
            </div>
        );
    };

    const renderList = () => (
        <div className="attachments-panel-item" data-testid="attachments-list">
            {renderPreview()}
            {attachments.map(renderAttachmentRow)}
            {renderAddAnother()}
        </div>
    );

    // XPEN-3118: only when 2+ distinct non-null owner_sub_id values. Null /
    // empty ids land in a labeled "general" section at the bottom. New
    // invoice uploads omit owner_sub_id and join that section.
    const isGrouped = shouldGroupAttachmentsByOwnerSubId(attachments);

    const attachmentCountLabel = (count) => (
        count === 1
            ? data.t.read('attachments_group_attachment_one')
            : data.t.read('attachments_group_attachment_other').replace('{n}', String(count))
    );

    const resolveGroupLabel = (ownerSubIdKey, attachmentCount) => {
        const countLabel = attachmentCountLabel(attachmentCount);
        const mapped = attachmentGroupLabels?.[ownerSubIdKey];
        if (mapped && typeof mapped === 'object' && Number.isInteger(mapped.lineNumber)) {
            const line = data.t.read('attachments_group_line').replace('{n}', String(mapped.lineNumber));
            const segments = [line];
            if (Array.isArray(mapped.details)) {
                for (const detail of mapped.details) {
                    if (typeof detail === 'string' && detail.trim()) segments.push(detail.trim());
                }
            }
            if (typeof mapped.amount === 'string' && mapped.amount.trim()) {
                segments.push(mapped.amount.trim());
            }
            segments.push(countLabel);
            return segments.join(' — ');
        }
        // Backend contract is string checksum; coerce so a non-string never
        // throws on .length / .slice during render.
        const key = String(ownerSubIdKey);
        const short = key.length > GROUP_FALLBACK_KEY_LENGTH
            ? `${key.slice(0, GROUP_FALLBACK_KEY_LENGTH)}…`
            : key;
        return `${data.t.read('attachments_group_fallback')} (${short}) — ${countLabel}`;
    };

    const renderGroupedList = () => {
        const { groups, unassigned } = partitionAttachmentsByOwnerSubId(attachments);
        const orderedGroups = sortAttachmentGroupsByLineNumber(groups, attachmentGroupLabels);
        return (
            <div className="attachments-panel-item" data-testid="attachments-grouped">
                {renderPreview()}
                {orderedGroups.map(({ key, items }) => (
                    <div key={key} className="attachments-group" data-testid={`attachments-group-${key}`}>
                        <div className="attachments-group-header" data-testid={`attachments-group-header-${key}`}>
                            {resolveGroupLabel(key, items.length)}
                        </div>
                        {items.map(renderAttachmentRow)}
                    </div>
                ))}
                {unassigned.length > 0 && (
                    <div className="attachments-group" data-testid="attachments-group-unassigned">
                        <div className="attachments-group-header" data-testid="attachments-group-header-unassigned">
                            {data.t.read('attachments_group_unassigned')}
                        </div>
                        {unassigned.map(renderAttachmentRow)}
                    </div>
                )}
                {renderAddAnother()}
            </div>
        );
    };

    return (
        <div className="attachments-panel" data-testid="attachments-panel">
            {loading ? (
                // Rendered inside the same fill-the-space wrapper as every other
                // state (XPEN-3075) — this used to be a separate early return
                // before the wrapper even existed, so the spinner never filled
                // its container either.
                <div
                    className="attachments-panel-item align-items-center justify-content-center"
                    role="status"
                    data-testid="attachments-loading"
                >
                    {data.t.read('loading_in_progress')}
                </div>
            ) : (
                <>
                    {error && (
                        <div className="alert alert-danger" role="alert" data-testid="attachments-error">
                            {error}
                        </div>
                    )}
                    {validationError && (
                        <div className="alert alert-warning" role="alert" data-testid="attachments-validation-error">
                            {validationError}
                        </div>
                    )}
                    {uploading && <div data-testid="attachments-uploading">{data.t.read('uploading')}</div>}
                    {/* Gated on hasLoadFailed specifically, not the shared `error` —
                        an unrelated upload/open/delete failure on an already-known-
                        empty record must not hide the only control that lets the
                        user pick a file. Only a failed *load* means "we don't
                        actually know whether this record is empty", which is the
                        one case where the upload invitation would be misleading. */}
                    {attachments.length === 0 && !hasLoadFailed && renderUploadZone()}
                    {attachments.length > 0 && isGrouped && renderGroupedList()}
                    {attachments.length === 1 && !isGrouped && renderSingleFile()}
                    {attachments.length > 1 && !isGrouped && renderList()}
                    {deleteCandidate && (
                        <Modal
                            title={data.t.read('attachments_delete_confirm_title')}
                            okAction={confirmDelete}
                            okText={data.t.read('delete')}
                            cancelAction={() => setDeleteCandidate(null)}
                            cancelText={data.t.read('cancel')}
                        >
                            {data.t.read('attachments_delete_confirm_message')}
                        </Modal>
                    )}
                    {infoAttachment && (
                        <Modal
                            title={data.t.read('attachments_info_title')}
                            cancelAction={() => setInfoAttachment(null)}
                            cancelText={data.t.read('close')}
                        >
                            <div data-testid="attachments-info-modal">
                                <div className="mb-2">
                                    <strong>{data.t.read('attachments_info_uploaded_on')}:</strong>{' '}
                                    <span data-testid="attachments-info-date">{formatDate(infoAttachment.created_at)}</span>
                                </div>
                                <div className="mb-2">
                                    <strong>{data.t.read('attachments_info_source')}:</strong>{' '}
                                    <span data-testid="attachments-info-source">
                                        {infoAttachment.source === 'ingestor'
                                            ? data.t.read('attachments_source_ingestor')
                                            : data.t.read('attachments_source_manual')}
                                    </span>
                                </div>
                                {/* "INTERNAL" (not source === 'ingestor') is the actual
                                    signal for "no real person uploaded this" — matching
                                    the backend's own placeholder (attachments_service.py)
                                    rather than today's incidental 1:1 correlation with
                                    ingestor source, which XPEN-3011 is expected to break.
                                    Name and email are two separate rows, not one row with
                                    a name-falls-back-to-email — uploaded_by_name (XPEN-3091)
                                    is only ever missing when the uploader's account has no
                                    first/last name set, in which case the name row alone
                                    is skipped; the email row still always shows. */}
                                {infoAttachment.uploaded_by && infoAttachment.uploaded_by !== 'INTERNAL' && (
                                    <>
                                        {infoAttachment.uploaded_by_name && (
                                            <div className="mb-2">
                                                <strong>{data.t.read('name')}:</strong>{' '}
                                                <span data-testid="attachments-info-uploaded-by-name">
                                                    {infoAttachment.uploaded_by_name}
                                                </span>
                                            </div>
                                        )}
                                        <div>
                                            <strong>{data.t.read('email')}:</strong>{' '}
                                            <span data-testid="attachments-info-uploaded-by-email">
                                                {infoAttachment.uploaded_by}
                                            </span>
                                        </div>
                                    </>
                                )}
                            </div>
                        </Modal>
                    )}
                </>
            )}
        </div>
    );
};

export default AttachmentsPanel;
